import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import api, {
  clearApiOrganizationScope,
  clearPersistedOrganizationScopeForUser,
  publicRequest,
  withoutOrganizationScope,
} from "../lib/api";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // AI capabilities from /api/health
  const [aiCapabilities, setAiCapabilities] = useState({
    backend: null,
    model: null,
    available: false,
    features: [],
  });
  // Application version and build time from /api/health; null until loaded or when the request fails
  const [appVersion, setAppVersion] = useState(null);

  // Check if user is authenticated via cookie
  const checkAuth = useCallback(async () => {
    try {
      const response = await api.get('/auth/me', withoutOrganizationScope());
      setUser(response.data);
      return true;
    } catch (error) {
      setUser(null);
      return false;
    }
  }, []);
  
  // Fetch AI capabilities from health endpoint
  const fetchAICapabilities = useCallback(async () => {
    try {
      const response = await api.get('/health', publicRequest({}));
      const data = response.data;
      setAiCapabilities({
        backend: data.ai_backend || null,
        model: data.ai_model || null,
        available: data.ai_available || false,
        features: data.ai_features_available || [],
      });
      if (typeof data.version === 'string') {
        setAppVersion({ version: data.version, builtAt: data.built_at || null });
      }
    } catch (error) {
      console.warn('Failed to fetch AI capabilities:', error);
      // Keep defaults
    }
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      await checkAuth();
      await fetchAICapabilities();
      setLoading(false);
    };
    initAuth();
  }, [checkAuth, fetchAICapabilities]);

  const login = async (email, password) => {
    // Use publicRequest to prevent 401 redirect (wrong password is normal)
    const response = await api.post('/auth/login', { email, password }, publicRequest({}));
    const { user: userData } = response.data;
    clearApiOrganizationScope();
    clearPersistedOrganizationScopeForUser(userData.id);
    setUser(userData);
    return userData;
  };

  const register = async (data) => {
    // Use publicRequest to prevent 401 redirect
    const response = await api.post('/auth/register', data, publicRequest({}));
    const { user: userData } = response.data;
    setUser(userData);
    return userData;
  };

  const logout = useCallback(async () => {
    // Disable the request scope before logout so a stale organization cannot
    // prevent the backend from clearing the authentication cookie.
    clearPersistedOrganizationScopeForUser(user?.id);
    clearApiOrganizationScope();
    try {
      // Call backend to clear server-side cookie
      await api.post('/auth/logout', {}, publicRequest({}));
    } catch (error) {
      // Ignore logout errors
    }
    setUser(null);
  }, [user?.id]);

  // getAuthHeaders is kept for backward compatibility but returns empty
  // since cookies are now sent automatically via withCredentials
  const getAuthHeaders = useCallback(() => ({}), []);

  // Token is no longer exposed - authentication is via httpOnly cookie
  const token = null;

  const value = useMemo(() => ({ 
    user, 
    token, 
    loading, 
    login, 
    register, 
    logout, 
    getAuthHeaders,
    aiCapabilities,
    appVersion,
  }), [user, token, loading, logout, getAuthHeaders, aiCapabilities, appVersion]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
